import { FileSize, SpeedUnit, TestResult } from '../types';

// Size of the single test file
const TEST_FILE_SIZE: FileSize = '100MB';
const TEST_DURATION_LIMIT_MS = 15000; // 15 seconds

// Maps FileSize to actual size in bytes
const fileSizeMap: Record<FileSize, number> = {
  '100MB': 100 * 1024 * 1024,
  // Keep other sizes in case they are needed elsewhere temporarily, but remove them later if unused
  '1MB': 1 * 1024 * 1024,
  '5MB': 5 * 1024 * 1024,
  '10MB': 10 * 1024 * 1024,
};

// Get URL for the 100MB test file
export const getTestFileUrl = (): string => {
  // Add timestamp to prevent caching
  const timestamp = new Date().getTime();
  // Always use the 100MB file
  return `/static/test_${TEST_FILE_SIZE.toLowerCase()}.bin?t=${timestamp}`;
};

// Determine appropriate unit based on speed value
export const getSpeedUnit = (speedInMbps: number): SpeedUnit => {
  if (speedInMbps < 1) {
    return 'Kbps';
  } else if (speedInMbps >= 1000) {
    return 'Gbps';
  } else {
    return 'Mbps';
  }
};

// Format speed value based on unit
export const formatSpeed = (speedInMbps: number): { value: number; unit: SpeedUnit } => {
  if (speedInMbps < 1) {
    // Convert to Kbps
    return {
      value: parseFloat((speedInMbps * 1000).toFixed(2)),
      unit: 'Kbps'
    };
  } else if (speedInMbps >= 1000) {
    // Convert to Gbps
    return {
      value: parseFloat((speedInMbps / 1000).toFixed(2)),
      unit: 'Gbps'
    };
  } else {
    // Keep as Mbps
    return {
      value: parseFloat(speedInMbps.toFixed(2)),
      unit: 'Mbps'
    };
  }
};

// Calculate speed in Mbps
export const calculateSpeed = (bytes: number, durationMs: number): number => {
  // Convert bytes to bits and ms to seconds
  const bits = bytes * 8;
  const seconds = durationMs / 1000;
  
  // Calculate Mbps (Megabits per second)
  const mbps = bits / seconds / (1024 * 1024);
  
  // Return raw Mbps value (will be formatted later)
  return mbps;
};

// Perform speed test with realtime speed updates and 15-second limit
export const performSpeedTest = async (
  onProgress?: (progress: number) => void,
  onRealtimeSpeed?: (speedData: { value: number; unit: SpeedUnit }) => void,
  externalSignal?: AbortSignal
): Promise<TestResult> => {
  const size = TEST_FILE_SIZE;
  const totalBytes = fileSizeMap[size];
  let controller = new AbortController();
  
  // Handle external AbortSignal if provided
  if (externalSignal) {
    if (externalSignal.aborted) {
      // Already aborted
      return {
        speed: null,
        error: 'Test was cancelled before starting.',
        time: null,
        size: TEST_FILE_SIZE,
        unit: 'Mbps',
        bytesReceived: 0,
        durationLimited: false
      };
    }
    
    // Listen for abort event on external signal
    externalSignal.addEventListener('abort', () => {
      controller.abort();
    });
  }
  
  const signal = controller.signal;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let testCompletedNaturally = false;

  try {
    const url = getTestFileUrl();
    const startTime = performance.now();

    // Set timeout to abort fetch after 15 seconds
    timeoutId = setTimeout(() => {
      console.log(`Test timeout reached after ${TEST_DURATION_LIMIT_MS / 1000} seconds.`);
      controller.abort();
    }, TEST_DURATION_LIMIT_MS);

    console.log(`Starting download of ${size} file from ${url}`);
    const response = await fetch(url, { signal });

    if (!response.ok) {
      clearTimeout(timeoutId);
      throw new Error(`HTTP error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      clearTimeout(timeoutId);
      throw new Error('Could not get reader for response body');
    }

    let receivedBytes = 0;
    let lastProgressTime = startTime;
    let lastReceivedBytes = 0;
    const speedUpdateInterval = 200; // ms

    while (true) {
      try {
        const { done, value } = await reader.read();

        if (done) {
           console.log('Download completed naturally.');
           testCompletedNaturally = true; // Mark that download finished before timeout
           break;
        }

        receivedBytes += value ? value.length : 0;
        const progress = (receivedBytes / totalBytes) * 100;
        if (onProgress) {
          onProgress(progress > 100 ? 100 : progress); // Cap progress at 100%
        }

        const currentTime = performance.now();
        if (currentTime - lastProgressTime >= speedUpdateInterval && onRealtimeSpeed) {
          const intervalBytes = receivedBytes - lastReceivedBytes;
          const intervalTime = currentTime - lastProgressTime;
          if (intervalTime > 0) {
            const instantSpeed = calculateSpeed(intervalBytes, intervalTime);
            const formattedSpeed = formatSpeed(instantSpeed);
            onRealtimeSpeed(formattedSpeed);
          }
          lastProgressTime = currentTime;
          lastReceivedBytes = receivedBytes;
        }

        // Check if time limit exceeded *during* the loop (extra safety)
        if (performance.now() - startTime >= TEST_DURATION_LIMIT_MS) {
            console.log('Timeout check inside loop triggered.');
            // No need to abort here, the timeout will handle it
            // Just break the loop to stop processing data
            break;
        }

      } catch (error) {
        // Handle read errors, including AbortError from timeout
        if (error instanceof DOMException && error.name === 'AbortError') {
           console.log('Fetch aborted due to timeout.');
        } else {
           console.error('Error reading stream:', error);
        }
        // Break the loop on any read error
        break;
      }
    }

    // Clear the timeout if the download finished or loop broke for other reasons
    if (timeoutId) {
       clearTimeout(timeoutId);
    }

    const endTime = performance.now();
    const duration = Math.min(endTime - startTime, TEST_DURATION_LIMIT_MS); // Use actual duration or limit

    console.log(`Test finished. Completed naturally: ${testCompletedNaturally}, Duration used for calc: ${duration.toFixed(2)}ms, Received bytes: ${receivedBytes}`);

    // Calculate speed based on bytes received within the duration
    const finalSpeedMbps = calculateSpeed(receivedBytes, duration);
    const { value: formattedSpeed, unit } = formatSpeed(finalSpeedMbps);

    return {
      speed: formattedSpeed,
      error: null,
      time: parseFloat(duration.toFixed(2)),
      size: size, // Report the target file size
      unit: unit,
      bytesReceived: receivedBytes, // Add how many bytes were actually received
      durationLimited: !testCompletedNaturally // Indicate if the test hit the time limit
    };

  } catch (error) {
     if (timeoutId) {
       clearTimeout(timeoutId);
     }
    // Handle fetch initiation errors or AbortError before reading starts
    if (error instanceof DOMException && error.name === 'AbortError') {
       console.log('Fetch aborted by timeout before reading started.');
       // Return a result indicating timeout with 0 speed
       return {
         speed: 0,
         error: 'Test timed out before starting download.',
         time: TEST_DURATION_LIMIT_MS,
         size: TEST_FILE_SIZE,
         unit: 'Mbps',
         bytesReceived: 0,
         durationLimited: true
       };
     }
    console.error('Speed test failed:', error);
    return {
      speed: null,
      error: error instanceof Error ? error.message : 'Unknown error during test setup.',
      time: null,
      size: TEST_FILE_SIZE,
      unit: 'Mbps',
      bytesReceived: 0,
      durationLimited: false // Not limited if setup failed
    };
  }
}; 